import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api';
import type { Fix, FixStatus } from '../types';

const COLS: { key: FixStatus; title: string; accent: string; headerColor: string }[] = [
  { key: 'reported',      title: 'Reported',      accent: 'border-coast-muted',   headerColor: 'text-coast-muted' },
  { key: 'investigating', title: 'Investigating', accent: 'border-semantic-amber', headerColor: 'text-semantic-amber' },
  { key: 'fixing',        title: 'Fixing',        accent: 'border-coast-blue',    headerColor: 'text-coast-blue' },
  { key: 'shipped',       title: 'Shipped',       accent: 'border-semantic-green', headerColor: 'text-semantic-green' },
];

type Props = {
  fixes: Fix[];
  currentUser: string;
  isAdmin: boolean;
  onChange: () => void;
};

export function FixesBoard({ fixes, currentUser, isAdmin, onChange }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const stripRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const handler = () => {
      const w = strip.clientWidth;
      const idx = Math.round(strip.scrollLeft / (w * 0.88));
      setActiveIdx(Math.min(COLS.length - 1, Math.max(0, idx)));
    };
    strip.addEventListener('scroll', handler, { passive: true });
    return () => strip.removeEventListener('scroll', handler);
  }, []);

  const jumpTo = (i: number) => {
    const strip = stripRef.current;
    if (!strip) return;
    strip.scrollTo({ left: Math.round(i * (strip.clientWidth * 0.88 + 12)), behavior: 'smooth' });
  };

  const advance = async (fix: Fix, dir: 1 | -1) => {
    const idx = COLS.findIndex(c => c.key === fix.status);
    const next = COLS[Math.min(COLS.length - 1, Math.max(0, idx + dir))];
    if (!next || next.key === fix.status) return;
    setBusyId(fix.id);
    try {
      await api.fixes.update(fix.id, { status: next.key });
      onChange();
    } finally { setBusyId(null); }
  };

  const addFix = async () => {
    if (!title.trim()) return;
    await api.fixes.create({ title, raisedBy: currentUser });
    setTitle('');
    setAdding(false);
    onChange();
  };

  return (
    <section>
      <header className="flex items-baseline justify-between mb-3 px-1">
        <h2 className="font-heading text-2xl">Fixes</h2>
        <button className="btn btn-ghost text-xs" onClick={() => setAdding(a => !a)}>
          {adding ? 'Cancel' : '+ Add'}
        </button>
      </header>

      <AnimatePresence>
        {adding && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-3 overflow-hidden px-1"
          >
            <input
              className="input mb-2"
              placeholder="Describe the fix / task…"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
              enterKeyHint="done"
              onKeyDown={e => { if (e.key === 'Enter') addFix(); }}
            />
            <button className="btn btn-primary btn-block" onClick={addFix}>Add Fix</button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="lg:hidden mb-2 flex justify-center">
        <div className="segmented">
          {COLS.map((c, i) => (
            <button
              key={c.key}
              aria-current={activeIdx === i}
              onClick={() => jumpTo(i)}
            >
              {c.title}
            </button>
          ))}
        </div>
      </div>

      <div ref={stripRef} className="col-strip">
        {COLS.map(col => {
          const here = fixes.filter(f => f.status === col.key);
          return (
            <div key={col.key} className={`border-t-4 ${col.accent} bg-coast-ink flex flex-col`}>
              <header className={`col-header ${col.accent} ${col.headerColor} bg-coast-black flex items-center justify-between`}>
                <span>{col.title}</span>
                <span className="text-xs text-coast-muted tabular-nums">{here.length}</span>
              </header>
              <div className="p-3 space-y-2 min-h-[180px]">
                {here.length === 0 && (
                  <div className="text-xs text-coast-muted italic py-6 text-center">—</div>
                )}
                {here.map(f => (
                  <motion.div key={f.id} layout className="card">
                    <div className="text-sm leading-snug">{f.title}</div>
                    {f.detail && f.detail !== f.title && (
                      <div className="text-xs text-coast-muted mt-1 line-clamp-3">{f.detail}</div>
                    )}
                    <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest text-coast-muted">
                      <span>By {f.raisedBy}</span>
                      {isAdmin && (
                        <span className="flex gap-1">
                          <button
                            className="chip"
                            disabled={busyId === f.id || col.key === 'reported'}
                            onClick={() => advance(f, -1)}
                            aria-label="Move back"
                          >←</button>
                          <button
                            className="chip"
                            disabled={busyId === f.id || col.key === 'shipped'}
                            onClick={() => advance(f, +1)}
                            aria-label="Move forward"
                          >→</button>
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
